import * as Sentry from '@sentry/react'
import { Button, Modal } from 'antd'
import React, { useEffect } from 'react'
import { useIdleTimer } from 'react-idle-timer'

enum IdleModalStates {
  NO_MODAL,
  COUNTDOWN,
  TOKEN_EXPIRED,
}

/**
 * This component handles access token refreshing for active users
 * as well as gracefully telling users about expiring access tokens due to inactivity.
 * @param tokenExpiration         When the access token will expire
 * @param validRefreshToken       Whether or not the currently stored refresh token can still
 *                                be used to exchange for a new access token
 * @param attemptGetAccessToken   A function that attempts to exchange the currently stored refresh token for an access token
 * @param redirectToLogin         A function that redirects the user to the login page
 */
export const AuthInactivityMonitor: React.FC<{
  tokenExpiration: Date | undefined
  validRefreshToken: boolean
  attemptGetAccessToken: () => void
  redirectToLogin: () => void
}> = ({
  tokenExpiration,
  validRefreshToken,
  attemptGetAccessToken,
  redirectToLogin,
}) => {
  // if the user is still active, how much time before the token expires before attempting to fetch a new one (10 mins)
  const refreshTokenThreshMilli = 1000 * 60 * 10
  // how much time before the token expires to start the modal countdown
  // this should be lower than `refreshTokenThreshMilli` so the modal doesn't pop up even if the user is still active (45 sec)
  const startIdleCountdownMilli = 1000 * 45
  // how much buffer time to put in the modal token countdown (5 sec)
  const countdownExpireBufferMilli = 1000 * 5
  // the delay between checking if the access token is expired (5 mins)
  const checkAccessTokenDelayMilli = 1000 * 60 * 5

  const [modalState, setModalState] = React.useState<IdleModalStates>(
    IdleModalStates.NO_MODAL
  )
  const [idleModalCountdown, setIdleModalCountdown] = React.useState<number>(0)

  const otherTabActive = React.useCallback(() => {
    setModalState(IdleModalStates.NO_MODAL)
    attemptGetAccessToken()
  }, [attemptGetAccessToken])

  const { message } = useIdleTimer({
    throttle:
      modalState === IdleModalStates.TOKEN_EXPIRED
        ? 100
        : checkAccessTokenDelayMilli,
    crossTab: true,
    onAction: () => {
      if (
        modalState === IdleModalStates.NO_MODAL &&
        tokenExpiration &&
        Date.now() > tokenExpiration.getTime() - refreshTokenThreshMilli
      ) {
        attemptGetAccessToken()
        message('', false)
      } else if (modalState === IdleModalStates.TOKEN_EXPIRED) {
        redirectToLogin()
      }
    },
    onMessage: otherTabActive,
  })

  // we trigger the countdown with a buffer time to accomodate for latency when requesting a new access token
  // for example: if the countdown has 10 seconds left and `countdownExpireBufferMilli` is 5000 milliseconds,
  // the access token is actually valid for 15 more seconds rather than 10
  useEffect(() => {
    if (tokenExpiration && tokenExpiration > new Date()) {
      const timeoutId = setTimeout(
        () => {
          Sentry.captureMessage('Starting countdown modal', {
            tags: { host: window.location.origin },
          })
          setModalState(IdleModalStates.COUNTDOWN)
          setIdleModalCountdown(Math.ceil(startIdleCountdownMilli / 1000))
        },
        tokenExpiration.getTime() -
          Date.now() -
          startIdleCountdownMilli -
          countdownExpireBufferMilli
      )

      return () => {
        clearTimeout(timeoutId)
      }
    } else if (tokenExpiration && tokenExpiration <= new Date()) {
      // the weird case where the auth provider component doesn't give us a fresh access token on render
      attemptGetAccessToken()
    }
  }, [
    tokenExpiration,
    startIdleCountdownMilli,
    countdownExpireBufferMilli,
    attemptGetAccessToken,
  ])

  // performs the countdown logic
  // will get cleaned up once we switch out of the countdown state
  useEffect(() => {
    let intervalId = null
    if (modalState === IdleModalStates.COUNTDOWN) {
      intervalId = setInterval(() => {
        setIdleModalCountdown((countdown) => countdown - 1)
      }, 1000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [modalState])

  // we check if the countdown is 0 in another effect
  // because it's hard to see the current countdown state within the setInterval function
  useEffect(() => {
    if (modalState === IdleModalStates.COUNTDOWN && idleModalCountdown <= 0) {
      Sentry.captureMessage('Modal countdown reached zero', {
        tags: { host: window.location.origin },
      })
      setModalState(IdleModalStates.TOKEN_EXPIRED)
    }
  }, [idleModalCountdown, modalState])

  // when we load "wake up" any other forecast tabs that may be timed out
  useEffect(() => {
    message('', false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!validRefreshToken && modalState !== IdleModalStates.TOKEN_EXPIRED) {
    Sentry.captureMessage('Detected invalid refresh token', {
      tags: { host: window.location.origin },
    })
    setModalState(IdleModalStates.TOKEN_EXPIRED)
  }

  switch (modalState) {
    case IdleModalStates.TOKEN_EXPIRED: {
      // legacy sentry message, will remove this when we're confident the sign-out problem is fixed
      Sentry.captureMessage('Showing token expired modal')

      if (document.hasFocus()) {
        redirectToLogin()
      }

      return <></>
    }
    case IdleModalStates.COUNTDOWN: {
      return (
        <Modal
          title='Are you still there?'
          open={true}
          footer={
            <Button
              type='primary'
              onClick={() => {
                setModalState(IdleModalStates.NO_MODAL)
                message('', false)
                attemptGetAccessToken()
              }}>
              {"I'm here!"}
            </Button>
          }
          closable={false}>
          <p>You will be disconnected in {idleModalCountdown} seconds</p>
        </Modal>
      )
    }
    default:
      return <></>
  }
}
